import { useState, useLayoutEffect, Fragment, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { v4 as uuidv4 } from 'uuid'
import AddExistingBranchModal from './AddExistingBranchModal'
import AddUser from '../Users/AddUser'
import UserSearchInput from '../common/UserSearchInput'
import RadioInput from '../common/RadioInput'
import Dropdown from '../common/Dropdown'
import SimpleTextInput from '../common/SimpleTextInput'
import PageModal from '../common/PageModal'
import SimpleButton from '../common/SimpleButton'
import GoogleAddressInput from '../common/GoogleAddressInput'
import ToggleSwitch from '../common/ToggleSwitch'
import ConfirmationModal from '../common/ConfirmationModal'
import { toast } from '../common/Toast'
import { createCompany, putCompany } from '../../actions/companies'
import countries from '../../utils/countries.json'
import { formatPhone } from '../../utils/functions'
import { TrashCanFull } from '../../icons'
import { companiesWritePermission } from '../../utils/permissionValidation'
import './companies-management-modal.scss'

const AddEditCompany = (props) => {
  const { t } = useTranslation()
  const { selectedCompany, closeModal, showSpinner, setShowSpinner } = props
  const [organizationType, setOrganizationType] = useState(null)
  const [accountType, setAccountType] = useState(null)
  const [companyStatus, setCompanyStatus] = useState('approved')
  const [companyName, setCompanyName] = useState('')
  const [adminEmail, setAdminEmail] = useState(null)
  const [phone, setPhone] = useState('')
  const [country, setCountry] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [stateProvince, setStateProvince] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [branches, setBranches] = useState([])
  const [isDentistCompany, setIsDentistCompany] = useState(true)
  const [haveError, setHaveError] = useState({})
  const [showDisablingConfirmationModal, setShowDisablingConfirmationModal] = useState(false)
  const [showAddUserModal, setShowAddUserModal] = useState(false)
  const [branchIdToAddUser, setBranchIdToAddUser] = useState(null)
  const [showAddExistingBranchModal, setShowAddExistingBranchModal] = useState(false)
  const individualOrganizationType = 'individual'
  const corporateOrganizationType = 'corporate'
  const endUserAccountType = 'End User'
  const dealerAccountType = 'Dealer'
  const phonePattern = new RegExp(/^[+]?[\s./0-9]*[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/)
  const emailPattern = new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)

  useLayoutEffect(() => {
    if (selectedCompany) {
      setAdminEmail(selectedCompany.email)
      setCompanyName(selectedCompany.companyName)
      setCompanyStatus(selectedCompany.status)
      setCountry(selectedCompany.country || '')
      setAddress(selectedCompany.officeAddressOne || '')
      setCity(selectedCompany.city || '')
      setStateProvince(selectedCompany.stateProvinceRegion || '')
      setZipCode(selectedCompany.zipPostalCode || '')
      setPhone(formatPhone(selectedCompany.telephone || ''))
      setIsDentistCompany(selectedCompany.account === endUserAccountType)
    }
  }, [selectedCompany])

  useEffect(() => {
    if (accountType) {
      setAdminEmail(null)
      setBranches([])
    }
  }, [accountType])

  useEffect(() => {
    if (organizationType) {
      setBranches([])
    }
  }, [organizationType])

  const companyHeaderContent = () => {
    return (
      <div className="status-wrapper">
        <div className="label">{t('status')}</div>
        <ToggleSwitch
          checked={companyStatus === 'approved'}
          onChange={() => setCompanyStatus((prevValue) => (prevValue === 'approved' ? 'disabled' : 'approved'))}
          isDisabled={companyStatus === 'pending' || isDentistCompany || showSpinner}
        />
        <div className="status">{t([`company.${companyStatus}`, companyStatus])}</div>
      </div>
    )
  }

  const companyFooterContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={() => closeModal()}>
          {t('cancel')}
        </SimpleButton>
        <SimpleButton
          className="submit-button"
          onClick={() => handleAddEditCompany()}
          disabled={showSpinner || hasSameInput()}
          requiredPermission={companiesWritePermission}
        >
          {t('save')}
        </SimpleButton>
      </>
    )
  }

  const handleOpenAddUserModal = () => {
    if (!accountType) {
      toast.error(t('error.account_type'))
      return
    }

    setShowAddUserModal(true)
  }

  const handleOpenAddExistingBranchModal = () => {
    if (!accountType) {
      toast.error(t('error.account_type'))
      return
    }

    setShowAddExistingBranchModal(true)
  }

  const handleAddUser = (newUserCreated) => {
    if (!branchIdToAddUser) {
      setAdminEmail(newUserCreated.email)
      setPhone(formatPhone(newUserCreated.phone || ''))
      setCountry(newUserCreated.country || '')
      setAddress(newUserCreated.addressLine1 || '')
      setCity(newUserCreated.city || '')
      setStateProvince(newUserCreated.state || '')
      setZipCode(newUserCreated.zip || '')
    }

    const updatedBranches = branches.map((branch) => {
      if (branch.id === branchIdToAddUser) {
        return {
          ...branch,
          email: newUserCreated.email,
          telephone: formatPhone(newUserCreated.phone || ''),
          country: newUserCreated.country || '',
          officeAddressOne: newUserCreated.addressLine1 || '',
          city: newUserCreated.city || '',
          stateProvinceRegion: newUserCreated.state || '',
          zipPostalCode: newUserCreated.zip || '',
        }
      }
      return branch
    })

    setBranches(updatedBranches)
  }

  const hasSameInput = () => {
    return (
      selectedCompany &&
      companyName === selectedCompany.companyName &&
      companyStatus === selectedCompany.status &&
      address === selectedCompany.officeAddressOne &&
      city === selectedCompany.city &&
      stateProvince === selectedCompany.stateProvinceRegion &&
      zipCode === selectedCompany.zipPostalCode &&
      phone === formatPhone(selectedCompany.telephone || '')
    )
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (!selectedCompany && !organizationType) {
      errors = { ...errors, organizationType: true }
      isValid = false
    }

    if (!selectedCompany && !accountType) {
      errors = { ...errors, accountType: true }
      isValid = false
    }

    if (!companyName.trim()) {
      errors = { ...errors, companyName: true }
      isValid = false
    }

    if (!adminEmail?.trim() || !emailPattern.test(adminEmail)) {
      errors = { ...errors, adminEmail: true }
      isValid = false
    }

    if (!phone.trim() || !phonePattern.test(phone)) {
      errors = { ...errors, phone: true }
      isValid = false
    }

    if (!country.trim()) {
      errors = { ...errors, country: true }
      isValid = false
    }

    if (!address.trim()) {
      errors = { ...errors, address: true }
      isValid = false
    }

    if (!city.trim()) {
      errors = { ...errors, city: true }
      isValid = false
    }

    if (!stateProvince.trim()) {
      errors = { ...errors, stateProvince: true }
      isValid = false
    }

    if (!zipCode.trim()) {
      errors = { ...errors, zipCode: true }
      isValid = false
    }

    branches.forEach((branch) => {
      if (branch.isNew) {
        if (!branch.branchName.trim()) {
          errors = { ...errors, [`branchName-${branch.id}`]: true }
          isValid = false
        }

        if (!branch.email.trim() || !emailPattern.test(branch.email)) {
          errors = { ...errors, [`adminEmail-${branch.id}`]: true }
          isValid = false
        }

        if (!branch.telephone.trim() || !phonePattern.test(branch.telephone)) {
          console.log('branch', branch.id, branch.telephone)
          errors = { ...errors, [`phone-${branch.id}`]: true }
          isValid = false
        }

        if (!branch.country.trim()) {
          errors = { ...errors, [`country-${branch.id}`]: true }
          isValid = false
        }

        if (!branch.officeAddressOne.trim()) {
          errors = { ...errors, [`address-${branch.id}`]: true }
          isValid = false
        }

        if (!branch.city.trim()) {
          errors = { ...errors, [`city-${branch.id}`]: true }
          isValid = false
        }

        if (!branch.stateProvinceRegion.trim()) {
          errors = { ...errors, [`stateProvince-${branch.id}`]: true }
          isValid = false
        }

        if (!branch.zipPostalCode.trim()) {
          errors = { ...errors, [`zipCode-${branch.id}`]: true }
          isValid = false
        }
      }
    })

    setHaveError(errors)
    return isValid
  }

  const handleAddEditCompany = () => {
    if (!isValidInput()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    if (selectedCompany?.status !== companyStatus && companyStatus === 'disabled') {
      setShowDisablingConfirmationModal(true)
      return
    }

    handleRequest()
  }

  const handleRequest = () => {
    let bodyParams = {
      companyName: companyName.trim(),
      email: adminEmail.trim(),
      telephone: phone.trim(),
      country: country.trim(),
      officeAddressOne: address.trim(),
      city: city.trim(),
      stateProvinceRegion: stateProvince.trim(),
      zipPostalCode: zipCode.trim(),
      status: companyStatus,
    }

    if (selectedCompany) {
      props.actions.putCompany(selectedCompany.id, bodyParams).then(() => {
        closeModal()
      })

      return
    }

    bodyParams = {
      ...bodyParams,
      isCorporation: organizationType === corporateOrganizationType,
      branches: branches.map((branch) => ({
        ...(branch.isNew
          ? {
              branchName: branch.branchName.trim(),
              email: branch.email.trim(),
              telephone: branch.telephone ? formatPhone(branch.telephone) : '',
              country: branch.country.trim(),
              officeAddressOne: branch.officeAddressOne.trim(),
              city: branch.city.trim(),
              stateProvinceRegion: branch.stateProvinceRegion.trim(),
              zipPostalCode: branch.zipPostalCode.trim(),
            }
          : { id: branch.id }),
      })),
    }

    props.actions.createCompany(bodyParams).then(() => {
      closeModal()
    })
  }

  // const handleAddNewBranch = () => {
  //   const newBranch = {
  //     id: uuidv4(),
  //     isNew: true,
  //     branchName: '',
  //     email: '',
  //     officeAddressOne: '',
  //     country: '',
  //     city: '',
  //     stateProvinceRegion: '',
  //     zipPostalCode: '',
  //     telephone: '',
  //   }
  //   setBranches([...branches, newBranch])
  // }

  const handleBranchChange = (id, field, value) => {
    const updatedBranches = branches.map((branch) => {
      if (branch.id === id) {
        return { ...branch, [field]: value }
      }
      return branch
    })
    setBranches(updatedBranches)
  }

  const mappedErrorsToSpecificBranch = (branchId) => {
    return Object.keys(haveError).reduce((acc, key) => {
      if (key.endsWith(`-${branchId}`)) {
        const newKey = key.replace(`-${branchId}`, '')
        acc[newKey] = haveError[key]
      }
      return acc
    }, {})
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={() => closeModal()}
        className="companies-management-modal"
        title={selectedCompany ? t('organization_context.edit_organization') : t('organization_context.add_organization')}
        headerContent={companyHeaderContent()}
        footerContent={companyFooterContent()}
      >
        <div className="companies-management-modal-content">
          {!selectedCompany && (
            <>
              <div className="section-title">{t('structure').toUpperCase()}</div>
              <div className="row-wrapper">
                <div className="input-wrapper">
                  <p className="input-option">{t('organization_context.organization_type')}*</p>
                  {haveError['organizationType'] && <div className="error-message">{t('error.organization_type')}</div>}
                  <div className="label with-large-gap">
                    <RadioInput
                      onChange={() => setOrganizationType(individualOrganizationType)}
                      checked={organizationType === individualOrganizationType}
                      text={t('organization_context.single_branch')}
                    />
                    <RadioInput
                      onChange={() => setOrganizationType(corporateOrganizationType)}
                      checked={organizationType === corporateOrganizationType}
                      text={t('corporate_dso')}
                    />
                  </div>
                </div>
                <div className="input-wrapper">
                  <p className="input-option">{t('account_context.account_type')}*</p>
                  {haveError['accountType'] && <div className="error-message">{t('error.account_type')}</div>}
                  <div className="label with-large-gap">
                    <RadioInput onChange={() => setAccountType(endUserAccountType)} checked={accountType === endUserAccountType} text={endUserAccountType} />
                    <RadioInput onChange={() => setAccountType(dealerAccountType)} checked={accountType === dealerAccountType} text={dealerAccountType} />
                  </div>
                </div>
              </div>
              <div className="separator" />
            </>
          )}
          <div className="section-title">{t('general_information').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('name')}*</p>
              <SimpleTextInput
                value={companyName}
                onChange={(value) => setCompanyName(value)}
                hasError={haveError['companyName']}
                onClear={() => setCompanyName('')}
              />
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">
                {t('admin')}*
                {!selectedCompany && (
                  <span className="action-label" onClick={() => handleOpenAddUserModal()}>
                    {' '}
                    {t('create_user')}
                  </span>
                )}
              </p>
              {!selectedCompany && (
                <UserSearchInput
                  initialSelectedKey={adminEmail}
                  onChange={(option) => setAdminEmail(option.key)}
                  hasError={haveError['adminEmail']}
                  defaultQueryParams={{ status: 'activated' }}
                  localFilters={{ account: accountType, branchName: null }}
                  setShowSpinner={setShowSpinner}
                  isDisabled={!accountType}
                />
              )}
              {selectedCompany && <div className="value">{adminEmail}</div>}
            </div>
          </div>
          <div className="separator" />
          <div className="section-title">{t('location').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('phone')}*</p>
              <SimpleTextInput value={formatPhone(phone)} onChange={(value) => setPhone(value)} hasError={haveError['phone']} onClear={() => setPhone('')} />
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('country')}*</p>
              <Dropdown
                className="dropdown-input"
                placeholder={t('users-management.select-country')}
                options={countries?.map((country) => ({ key: country['code'], value: country['name'] }))}
                initialSelectedKey={country}
                onChange={(country) => setCountry(country.key)}
                hasError={haveError['country']}
                withFiltering
              />
            </div>
          </div>
          <GoogleAddressInput
            country={country}
            address={address}
            handleSetAddress={(e) => setAddress(e)}
            city={city}
            handleSetCity={(e) => setCity(e)}
            stateProvince={stateProvince}
            handleSetStateProvince={(e) => setStateProvince(e)}
            zipCode={zipCode}
            handleSetZipCode={(e) => setZipCode(e)}
            errors={haveError}
            withoutPlaceholders
          />
          {!selectedCompany && (
            <>
              <div className="separator" />
              <div className="section-title">{t('organization_context.branches_single_multiple').toUpperCase()}</div>
              {(!organizationType || organizationType === individualOrganizationType) && (
                <div className="row-wrapper">
                  <div className="input-wrapper">
                    <div className="disclaimer">
                      <p>{t('organization_context.individual_organization_branches_disclaimer')}</p>
                    </div>
                  </div>
                </div>
              )}
              {organizationType === corporateOrganizationType && (
                <>
                  <div className="row-wrapper">
                    <div className="input-wrapper">
                      <div className="label">
                        {/* <button className="add-btn" onClick={() => handleAddNewBranch()}>
                          + {t('organization_context.add_new_branch')}
                        </button>
                        <span>{t('or')}</span> */}
                        <button className="add-btn" onClick={() => handleOpenAddExistingBranchModal()}>
                          {t('organization_context.choose_existing_branch')}
                        </button>
                      </div>
                    </div>
                  </div>
                  {branches.map((branch, index) => (
                    <Fragment key={index}>
                      {index !== 0 && <div className="inner-separator" />}
                      <div className="row-wrapper">
                        <div className="input-wrapper">
                          <p className="input-option">
                            {`${t('name')}*`}
                            <TrashCanFull className="delete-icon " onClick={() => setBranches(branches.filter((b) => b.id !== branch.id))} />
                          </p>
                          {branch.isNew ? (
                            <SimpleTextInput
                              value={branch.branchName}
                              onChange={(value) => handleBranchChange(branch.id, 'branchName', value)}
                              hasError={haveError[`branchName-${branch.id}`]}
                              onClear={() => handleBranchChange(branch.id, 'branchName', '')}
                            />
                          ) : (
                            <div className="value">{branch.branchName}</div>
                          )}
                        </div>
                      </div>
                      <div className="row-wrapper">
                        <div className="input-wrapper">
                          <p className="input-option">
                            {t('organization_context.branch_admin')}*
                            {branch.isNew && (
                              <span
                                className="action-label"
                                onClick={() => {
                                  setBranchIdToAddUser(branch.id)
                                  handleOpenAddUserModal()
                                }}
                              >
                                {' '}
                                {t('create_user')}
                              </span>
                            )}
                          </p>
                          {branch.isNew ? (
                            <UserSearchInput
                              initialSelectedKey={branch.email}
                              onChange={(admin) => handleBranchChange(branch.id, 'email', admin.key)}
                              hasError={haveError[`adminEmail-${branch.id}`]}
                              defaultQueryParams={{ status: 'activated' }}
                              localFilters={{ account: accountType, branchName: null }}
                              setShowSpinner={setShowSpinner}
                            />
                          ) : (
                            <div className="value">{branch.email}</div>
                          )}
                        </div>
                      </div>
                      <div className="row-wrapper">
                        <div className="input-wrapper">
                          <p className="input-option">{t('phone')}*</p>
                          {branch.isNew ? (
                            <SimpleTextInput
                              value={formatPhone(branch.telephone || '')}
                              onChange={(value) => handleBranchChange(branch.id, 'telephone', formatPhone(value))}
                              hasError={haveError[`phone-${branch.id}`]}
                              onClear={() => handleBranchChange(branch.id, 'telephone', '')}
                            />
                          ) : (
                            <div className="value">{formatPhone(branch.telephone || '')}</div>
                          )}
                        </div>
                        <div className="input-wrapper">
                          <p className="input-option">{t('country')}*</p>
                          {branch.isNew ? (
                            <Dropdown
                              className="dropdown-input"
                              placeholder={t('users-management.select-country')}
                              options={countries?.map((country) => ({ key: country['code'], value: country['name'] }))}
                              initialSelectedKey={branch.country}
                              onChange={(country) => handleBranchChange(branch.id, 'country', country.key)}
                              hasError={haveError[`country-${branch.id}`]}
                              withFiltering
                            />
                          ) : (
                            <div className="value">{countries?.find((c) => c.code === branch.country)?.name}</div>
                          )}
                        </div>
                      </div>
                      {branch.isNew ? (
                        <GoogleAddressInput
                          country={branch.country}
                          address={branch.officeAddressOne}
                          handleSetAddress={(e) => handleBranchChange(branch.id, 'officeAddressOne', e)}
                          city={branch.city}
                          handleSetCity={(e) => handleBranchChange(branch.id, 'city', e)}
                          stateProvince={branch.stateProvinceRegion}
                          handleSetStateProvince={(e) => handleBranchChange(branch.id, 'stateProvinceRegion', e)}
                          zipCode={branch.zipPostalCode}
                          handleSetZipCode={(e) => handleBranchChange(branch.id, 'zipPostalCode', e)}
                          errors={mappedErrorsToSpecificBranch(branch.id)}
                          withoutPlaceholders
                          useLocalData={true}
                        />
                      ) : (
                        <div className="row-wrapper">
                          <div className="input-wrapper">
                            <p className="input-option">{t('address')}</p>
                            <div className="value">{branch.officeAddressOne}</div>
                          </div>
                          <div className="input-wrapper">
                            <p className="input-option">{t('city')}</p>
                            <div className="value">{branch.city}</div>
                          </div>
                          <div className="input-wrapper">
                            <p className="input-option">{t('zip_postal_code')}</p>
                            <div className="value">{branch.zipPostalCode}</div>
                          </div>
                        </div>
                      )}
                    </Fragment>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </PageModal>
      {showDisablingConfirmationModal && (
        <ConfirmationModal
          className="invalidate-confirmation-modal"
          onToggle={() => setShowDisablingConfirmationModal(false)}
          message={t('organization_context.accept_disable_confirmation')}
          onCancel={() => setShowDisablingConfirmationModal(false)}
          onAccept={() => {
            handleRequest()
            setShowDisablingConfirmationModal(false)
          }}
          requiredPermission={companiesWritePermission}
        >
          <div className="invalidate-confirmation-wrapper">
            <div className="disclaimer">
              <p>{t('organization_context.accept_disable_disclaimer')}</p>
            </div>
          </div>
        </ConfirmationModal>
      )}
      <div className="users-management-wrapper">
        {showAddUserModal && (
          <AddUser
            accountType={accountType}
            onCreateUser={(user) => handleAddUser(user)}
            closeModal={() => setShowAddUserModal(false)}
            showSpinner={showSpinner}
            setShowSpinner={setShowSpinner}
          />
        )}
      </div>
      {showAddExistingBranchModal && (
        <AddExistingBranchModal
          accountType={accountType}
          selectedBranches={branches}
          handleUpdateBranches={(newBranches) => setBranches(newBranches)}
          closeModal={() => setShowAddExistingBranchModal(false)}
        />
      )}
    </>
  )
}

function stateToProps({}) {
  return {}
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        createCompany,
        putCompany,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(AddEditCompany)

